import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import { 
  Sparkles, Droplets, Sun, Moon, FlaskConical, ShieldCheck, ArrowRight, Check, CheckCircle,
  Camera, Link, FileText, Search, AlertTriangle, Clock, Zap, Users, Star, Heart,
  Instagram, Mail, Palette, Smile, Gift, Wand2
} from "lucide-react";

export default function LandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated, login, register, token, updateUser } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [registrationStep, setRegistrationStep] = useState(1);
  const [selectedSkinType, setSelectedSkinType] = useState(null);
  const [newUserToken, setNewUserToken] = useState(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, navigate]);

  if (isAuthenticated) {
    return null;
  }

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await login(formData.email, formData.password);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error("Please enter your email");
      return;
    }
    setIsLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
      toast.success("Check your email for reset instructions");
    } catch (error) {
      toast.error("Failed to send reset email. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const userData = await register(formData.email, formData.password);
      setNewUserToken(localStorage.getItem("skiniq_token"));
      setRegistrationStep(2);
      toast.success("Account created! Now let's set up your skin type.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkinTypeSelect = async (skinType) => {
    setSelectedSkinType(skinType);
    setIsLoading(true);
    try {
      const tokenToUse = newUserToken || token;
      await axios.put(`${API}/auth/skin-type`, 
        { skin_type: skinType },
        { headers: { Authorization: `Bearer ${tokenToUse}` } }
      );
      updateUser({ skin_type: skinType });
      toast.success("You're all set! Welcome to SkinIQ.");
      setShowAuth(false);
      setRegistrationStep(1);
      setSelectedSkinType(null);
      navigate("/dashboard");
    } catch (error) {
      toast.error("Failed to set skin type. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const skinTypes = [
    { id: "dry", name: "Dry", description: "Often feels tight, may have flaky patches" },
    { id: "oily", name: "Oily", description: "Shiny appearance, enlarged pores" },
    { id: "combination", name: "Combination", description: "Oily T-zone, dry cheeks" }
  ];

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-border/50">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Droplets className="w-8 h-8 text-[hsl(168,55%,45%)]" />
            <span className="font-serif text-2xl font-medium bg-gradient-to-r from-[hsl(168,55%,40%)] to-[hsl(340,65%,60%)] bg-clip-text text-transparent">SkinIQ</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollToSection('how-it-works')} className="text-sm text-foreground/70 hover:text-[hsl(340,65%,60%)] transition-colors">How It Works</button>
            <button onClick={() => scrollToSection('features')} className="text-sm text-foreground/70 hover:text-[hsl(340,65%,60%)] transition-colors">Features</button>
            <button onClick={() => scrollToSection('why-skiniq')} className="text-sm text-foreground/70 hover:text-[hsl(340,65%,60%)] transition-colors">Why SkinIQ</button>
            <button onClick={() => scrollToSection('faq')} className="text-sm text-foreground/70 hover:text-[hsl(340,65%,60%)] transition-colors">FAQ</button>
          </div>
          <Button 
            onClick={() => setShowAuth(true)}
            className="btn-primary"
            data-testid="nav-get-started-btn"
          >
            Get Started Free
          </Button>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="gradient-hero pt-24">
        <div className="container mx-auto px-6 py-16 lg:py-24">
          <div className="relative flex flex-col lg:flex-row items-center">
            {/* Image on the right */}
            <div className="lg:absolute lg:right-0 lg:top-1/2 lg:-translate-y-1/2 lg:w-[60%] animate-fade-in">
              <img 
                src="https://customer-assets.emergentagent.com/job_d1bc58d2-12f5-4e2b-9714-781ce627199e/artifacts/d0a867jb_Gemini_Generated_Image_898ara898ara898a%20%281%29.png" 
                alt="Skincare products collection" 
                className="w-full h-auto object-contain rounded-3xl"
              />
            </div>
            
            {/* Text content */}
            <div className="relative z-10 lg:w-[35%] lg:ml-[10%] animate-fade-in-up">
              <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 lg:p-10 shadow-xl border border-white/50">
                <div className="inline-flex items-center gap-2 bg-[hsl(340,65%,95%)] rounded-full px-4 py-2 mb-6">
                  <Sparkles className="w-4 h-4 text-[hsl(340,65%,60%)]" />
                  <span className="text-sm font-medium text-[hsl(340,65%,50%)]">AI-Powered Skincare Magic</span>
                </div>
                <h1 className="font-serif text-4xl sm:text-5xl font-medium leading-tight mb-6">
                  <span className="text-[hsl(168,55%,35%)]">Stop Guessing.</span><br />
                  <span className="bg-gradient-to-r from-[hsl(340,65%,60%)] to-[hsl(340,70%,50%)] bg-clip-text text-transparent">Start Glowing.</span>
                </h1>
                <p className="text-base text-foreground/70 mb-8 leading-relaxed">
                  Upload your skincare products. Our AI analyzes every ingredient, flags what clashes, and builds your perfect morning and evening routine — personalized to your skin type.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button 
                    onClick={() => setShowAuth(true)} 
                    className="btn-primary text-base px-8 py-5"
                    data-testid="hero-get-started-btn"
                  >
                    Build My Routine Free
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    onClick={() => scrollToSection('how-it-works')}
                    className="text-[hsl(168,55%,40%)] hover:bg-[hsl(168,55%,95%)] px-6 py-5"
                    data-testid="hero-learn-more-btn"
                  >
                    See How It Works
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Trust Strip */}
        <div className="container mx-auto px-6 pb-12">
          <div className="flex flex-wrap justify-center gap-6 lg:gap-12 text-sm text-foreground/60">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-[hsl(168,55%,45%)]" />
              <span>Powered by dermatology-grade science</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-[hsl(340,65%,60%)]" />
              <span>100% free to start</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-[hsl(45,90%,50%)]" />
              <span>Works with any brand</span>
            </div>
          </div>
        </div>
      </div>

      {/* Social Proof Bar */}
      <section className="py-12 gradient-fun text-white">
        <div className="container mx-auto px-6">
          <p className="text-center text-white/80 mb-8 text-sm uppercase tracking-wider flex items-center justify-center gap-2">
            <Heart className="w-4 h-4" /> Loved by skincare enthusiasts everywhere <Heart className="w-4 h-4" />
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="font-serif text-4xl font-medium">2,400+</p>
              <p className="text-white/70 text-sm mt-1">Products Analyzed</p>
            </div>
            <div>
              <p className="font-serif text-4xl font-medium">15,000+</p>
              <p className="text-white/70 text-sm mt-1">Ingredients Rated</p>
            </div>
            <div>
              <p className="font-serif text-4xl font-medium">98%</p>
              <p className="text-white/70 text-sm mt-1">Happy Users</p>
            </div>
            <div>
              <p className="font-serif text-4xl font-medium">5 min</p>
              <p className="text-white/70 text-sm mt-1">To Your Perfect Routine</p>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-24 px-6 bg-white">
        <div className="container mx-auto max-w-4xl">
          <div className="flex items-center justify-center gap-2 mb-4">
            <AlertTriangle className="w-5 h-5 text-[hsl(340,65%,60%)]" />
            <p className="text-sm text-[hsl(340,65%,60%)] font-medium uppercase tracking-wider">The Skincare Struggle is Real</p>
          </div>
          <h2 className="font-serif text-3xl lg:text-4xl font-medium text-[hsl(168,55%,30%)] mb-8 text-center">
            Great Products. Zero Clue How to Use Them Together.
          </h2>
          <div className="prose prose-lg max-w-none text-foreground/70">
            <p className="mb-6 text-center">
              Your bathroom shelf is basically a chemistry lab at this point. Retinol, vitamin C, niacinamide, hyaluronic acid... but here's the tea:
            </p>
            <p className="text-[hsl(168,55%,35%)] font-medium text-xl mb-8 text-center">
              Using the wrong products together can cancel them out — or worse, wreck your skin barrier!
            </p>
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                { text: "Retinol + AHA/BHA same night = damaged skin barrier", color: "hsl(340,65%,95%)", icon: "hsl(340,65%,60%)" },
                { text: "Vitamin C at night = wasted antioxidant power", color: "hsl(45,90%,92%)", icon: "hsl(45,85%,45%)" },
                { text: "5 serums when you need 2 = breakouts & wasted $$$", color: "hsl(168,50%,92%)", icon: "hsl(168,55%,45%)" },
                { text: "Duplicate ingredients across products = why tho?", color: "hsl(280,60%,95%)", icon: "hsl(280,60%,55%)" }
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 rounded-xl p-4" style={{ backgroundColor: item.color }}>
                  <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: item.icon }} />
                  <span className="text-sm text-foreground/80">{item.text}</span>
                </div>
              ))}
            </div>
            <p className="text-center text-xl font-medium mb-8">
              <span className="text-[hsl(168,55%,35%)]">You don't need more products.</span>{" "}
              <span className="text-[hsl(340,65%,55%)]">You need a smarter routine!</span>
            </p>
            <div className="text-center">
              <Button onClick={() => setShowAuth(true)} className="btn-pink text-lg px-10 py-6">
                <Wand2 className="w-5 h-5 mr-2" />
                Let SkinIQ Work Its Magic
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-24 px-6 bg-[hsl(168,40%,97%)]">
        <div className="container mx-auto">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-[hsl(340,65%,60%)]" />
            <p className="text-sm text-[hsl(340,65%,60%)] font-medium uppercase tracking-wider">Easy Peasy</p>
          </div>
          <h2 className="font-serif text-3xl lg:text-4xl font-medium text-[hsl(168,55%,30%)] mb-16 text-center">
            From Shelf Chaos to Perfect Routine in Minutes
          </h2>
          
          <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* Step 1 */}
            <Card className="rounded-3xl border-0 shadow-lg overflow-hidden hover-lift">
              <div className="h-2 bg-gradient-to-r from-[hsl(168,55%,45%)] to-[hsl(168,60%,55%)]" />
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[hsl(168,55%,45%)] to-[hsl(168,60%,35%)] text-white flex items-center justify-center text-2xl font-serif mb-6 shadow-lg">
                  01
                </div>
                <h3 className="font-serif text-2xl font-medium text-[hsl(168,55%,30%)] mb-4">Add Your Products</h3>
                <p className="text-foreground/70 mb-6">
                  Snap a photo, paste a URL, search by name, or copy ingredients. Our AI figures out the rest!
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { icon: Camera, text: "Photo", color: "hsl(340,65%,60%)" },
                    { icon: Link, text: "URL", color: "hsl(168,55%,45%)" },
                    { icon: Search, text: "Search", color: "hsl(45,85%,50%)" },
                    { icon: FileText, text: "Paste", color: "hsl(280,60%,60%)" }
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm text-foreground/60 bg-white/50 rounded-lg p-2">
                      <item.icon className="w-4 h-4" style={{ color: item.color }} />
                      <span>{item.text}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Step 2 */}
            <Card className="rounded-3xl border-0 shadow-lg overflow-hidden hover-lift">
              <div className="h-2 bg-gradient-to-r from-[hsl(340,65%,60%)] to-[hsl(340,70%,70%)]" />
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[hsl(340,65%,60%)] to-[hsl(340,70%,50%)] text-white flex items-center justify-center text-2xl font-serif mb-6 shadow-lg">
                  02
                </div>
                <h3 className="font-serif text-2xl font-medium text-[hsl(168,55%,30%)] mb-4">See What's Inside</h3>
                <p className="text-foreground/70 mb-6">
                  Every ingredient identified, rated, and explained. Find conflicts before your skin does!
                </p>
                <ul className="space-y-2">
                  {[
                    { text: "Star ratings (1-5 scale)", color: "hsl(45,90%,50%)" },
                    { text: "Conflict warnings", color: "hsl(340,65%,60%)" },
                    { text: "Duplicate ingredient alerts", color: "hsl(168,55%,45%)" },
                    { text: "Power ingredient highlights", color: "hsl(280,60%,60%)" }
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-foreground/60">
                      <Check className="w-4 h-4" style={{ color: item.color }} />
                      <span>{item.text}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Step 3 */}
            <Card className="rounded-3xl border-0 shadow-lg overflow-hidden hover-lift">
              <div className="h-2 bg-gradient-to-r from-[hsl(45,90%,55%)] to-[hsl(45,85%,65%)]" />
              <CardContent className="p-8">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[hsl(45,90%,55%)] to-[hsl(45,85%,45%)] text-white flex items-center justify-center text-2xl font-serif mb-6 shadow-lg">
                  03
                </div>
                <h3 className="font-serif text-2xl font-medium text-[hsl(168,55%,30%)] mb-4">Get Your Routine</h3>
                <p className="text-foreground/70 mb-6">
                  Custom AM & PM routines using YOUR products. Know exactly what to use and when!
                </p>
                <ul className="space-y-2">
                  {[
                    { text: "Exact product order", color: "hsl(168,55%,45%)" },
                    { text: "Daily vs alternate days", color: "hsl(340,65%,60%)" },
                    { text: "Conflict alerts", color: "hsl(45,85%,50%)" },
                    { text: "Keep/Alternate/Hold system", color: "hsl(280,60%,60%)" }
                  ].map((item, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-foreground/60">
                      <Check className="w-4 h-4" style={{ color: item.color }} />
                      <span>{item.text}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-12">
            <Button onClick={() => setShowAuth(true)} className="btn-primary text-lg px-10 py-6">
              <Gift className="w-5 h-5 mr-2" />
              Build My Routine Now
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-6 bg-white">
        <div className="container mx-auto">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Palette className="w-5 h-5 text-[hsl(168,55%,45%)]" />
            <p className="text-sm text-[hsl(168,55%,45%)] font-medium uppercase tracking-wider">Feature-Packed</p>
          </div>
          <h2 className="font-serif text-3xl lg:text-4xl font-medium text-[hsl(168,55%,30%)] mb-16 text-center">
            Everything Your Skincare Shelf Has Been Missing
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                icon: FlaskConical,
                title: "AI Ingredient Analysis",
                description: "Our AI reads labels, cross-references databases, and rates every ingredient. No more midnight Googling sessions!",
                color: "hsl(168,55%,45%)",
                bg: "hsl(168,50%,95%)"
              },
              {
                icon: ShieldCheck,
                title: "Conflict Detection",
                description: "Retinol + BHA same night? We catch these mistakes before your skin does. Every conflict explained + fixed!",
                color: "hsl(340,65%,60%)",
                bg: "hsl(340,60%,97%)"
              },
              {
                icon: Sun,
                title: "AM & PM Routines",
                description: "Not a generic template. YOUR products, real dermatology rules. Thinnest to thickest, actives at the right time.",
                color: "hsl(45,90%,50%)",
                bg: "hsl(45,80%,95%)"
              },
              {
                icon: Zap,
                title: "Keep / Alternate / Hold",
                description: "Own 15 products but can only use 6 daily? We sort everything so nothing goes to waste!",
                color: "hsl(280,60%,60%)",
                bg: "hsl(280,50%,97%)"
              },
              {
                icon: Users,
                title: "Skin Type Matching",
                description: "Dry, Oily, or Combo — every recommendation adjusts to YOUR skin. No one-size-fits-all advice here!",
                color: "hsl(16,80%,60%)",
                bg: "hsl(16,70%,96%)"
              },
              {
                icon: Star,
                title: "Works With Any Brand",
                description: "Paula's Choice, CeraVe, The Ordinary, K-beauty, drugstore, prestige — if it exists, we analyze it!",
                color: "hsl(200,70%,50%)",
                bg: "hsl(200,60%,96%)"
              }
            ].map((feature, idx) => (
              <Card key={idx} className="rounded-3xl border-0 shadow-md hover:shadow-xl transition-all hover:-translate-y-1">
                <CardContent className="p-8">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6" style={{ backgroundColor: feature.bg }}>
                    <feature.icon className="w-7 h-7" style={{ color: feature.color }} />
                  </div>
                  <h3 className="font-serif text-xl font-medium text-[hsl(168,55%,30%)] mb-3">{feature.title}</h3>
                  <p className="text-sm text-foreground/70 leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why SkinIQ Section */}
      <section id="why-skiniq" className="py-24 px-6 bg-gradient-to-br from-[hsl(168,40%,95%)] via-[hsl(340,40%,97%)] to-[hsl(45,50%,96%)]">
        <div className="container mx-auto max-w-4xl">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Smile className="w-5 h-5 text-[hsl(340,65%,60%)]" />
            <p className="text-sm text-[hsl(340,65%,60%)] font-medium uppercase tracking-wider">Made for You</p>
          </div>
          <h2 className="font-serif text-3xl lg:text-4xl font-medium text-[hsl(168,55%,30%)] mb-8 text-center">
            For Anyone Who's Serious About Their Skin
          </h2>
          <p className="text-lg text-foreground/70 mb-8 text-center max-w-2xl mx-auto">
            Whether you're a skincare newbie or a product hoarder (no judgment!), SkinIQ is here to help you get the most out of every bottle.
          </p>
          <div className="grid md:grid-cols-2 gap-4 mb-12">
            {[
              { title: "Clarity over confusion", desc: "Know exactly what to use, when, and why", color: "hsl(168,55%,45%)" },
              { title: "Results that actually work", desc: "The right products in the right order", color: "hsl(340,65%,60%)" },
              { title: "No wasted products", desc: "Every bottle gets a purpose or sits on the bench", color: "hsl(45,90%,50%)" },
              { title: "Plain English explanations", desc: "No confusing chemistry jargon", color: "hsl(280,60%,60%)" },
              { title: "A routine that fits YOUR life", desc: "Not a 14-step marathon", color: "hsl(200,70%,50%)" }
            ].map((item, idx) => (
              <div key={idx} className="flex items-start gap-3 bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
                <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: item.color }} />
                <div>
                  <span className="font-medium text-[hsl(168,55%,30%)]">{item.title}</span>
                  <span className="text-foreground/60"> — {item.desc}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Button onClick={() => setShowAuth(true)} className="btn-primary text-lg px-10 py-6">
              <Heart className="w-5 h-5 mr-2" />
              Start My Skincare Journey
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 px-6 bg-white">
        <div className="container mx-auto max-w-3xl">
          <h2 className="font-serif text-3xl lg:text-4xl font-medium text-[hsl(168,55%,30%)] mb-12 text-center">
            Got Questions? We've Got Answers!
          </h2>
          
          <Accordion type="single" collapsible className="space-y-4">
            {[
              {
                q: "Is SkinIQ really free?",
                a: "Yes! Add products, see ingredients, and get your personalized routine at no cost. We're growing and want your feedback — your experience helps us build something amazing for everyone.",
                color: "hsl(168,55%,45%)"
              },
              {
                q: "How does SkinIQ know what's in my products?",
                a: "We cross-reference professional databases including CosIng, Open Beauty Facts, and more. Upload a photo and our AI reads the label. We verify everything across multiple sources!",
                color: "hsl(340,65%,60%)"
              },
              {
                q: "Will this work with MY products?",
                a: "If a product exists and has an ingredient list, we can analyze it. Drugstore to prestige, K-beauty to French pharmacy — we've got you covered!",
                color: "hsl(45,90%,50%)"
              },
              {
                q: "What if I have sensitive skin?",
                a: "Your skin type selection influences every recommendation. We flag potentially irritating combos (like stacking retinol with chemical exfoliants) regardless of skin type.",
                color: "hsl(280,60%,60%)"
              },
              {
                q: "I have WAY more products than I can use daily...",
                a: "That's exactly what Keep/Alternate/Hold is for! We sort your products into daily essentials, weekly rotations, and products to set aside. Nothing goes to waste!",
                color: "hsl(200,70%,50%)"
              },
              {
                q: "How is this different from ChatGPT?",
                a: "ChatGPT gives generic advice. SkinIQ analyzes YOUR specific products, reads YOUR ingredient lists, detects conflicts between YOUR products, and builds a routine based on YOUR shelf. It's like having a skincare bestie who's also a dermatologist!",
                color: "hsl(16,80%,60%)"
              },
              {
                q: "Can I update my products later?",
                a: "Absolutely! Add new products, remove finished ones, and your routine rebuilds automatically. Your shelf changes — your routine should too!",
                color: "hsl(168,55%,45%)"
              }
            ].map((faq, idx) => (
              <AccordionItem key={idx} value={`faq-${idx}`} className="border-2 rounded-2xl px-6 hover:border-[hsl(168,55%,80%)] transition-colors" style={{ borderColor: "hsl(220,13%,91%)" }}>
                <AccordionTrigger className="text-left font-medium text-[hsl(168,55%,30%)] hover:no-underline py-6">
                  <span className="flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: faq.color }} />
                    {faq.q}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-foreground/70 pb-6">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 px-6 gradient-fun text-white">
        <div className="container mx-auto text-center max-w-3xl">
          <h2 className="font-serif text-3xl lg:text-4xl font-medium mb-6">
            Your Products Deserve a Better Routine. So Do You!
          </h2>
          <p className="text-white/80 mb-8 text-lg max-w-2xl mx-auto">
            You already own the products. Now let SkinIQ sort through the science, catch the conflicts, and build a routine that actually makes sense.
          </p>
          <p className="text-white/60 mb-8">
            No subscription. No credit card. No complicated tutorials.<br />
            Just upload your products and get glowing!
          </p>
          <Button 
            onClick={() => setShowAuth(true)} 
            className="bg-white text-[hsl(168,55%,35%)] hover:bg-white/90 text-lg px-12 py-7 rounded-full font-medium shadow-xl"
            data-testid="cta-get-started-btn"
          >
            <Sparkles className="w-5 h-5 mr-2" />
            Build My Free Routine
          </Button>
          <p className="text-white/50 text-sm mt-4">
            Takes less than 5 minutes. Seriously, that's it!
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-6 bg-[#F9F9F7] border-t border-border">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-4 gap-12">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Droplets className="w-6 h-6 text-[hsl(168,55%,45%)]" />
                <span className="font-serif text-xl font-medium bg-gradient-to-r from-[hsl(168,55%,40%)] to-[hsl(340,65%,60%)] bg-clip-text text-transparent">SkinIQ</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Your skin, intelligently cared for.
              </p>
            </div>
            
            {/* Product */}
            <div>
              <h4 className="font-medium text-[hsl(168,55%,30%)] mb-4">Product</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><button onClick={() => scrollToSection('how-it-works')} className="hover:text-[hsl(340,65%,60%)] transition-colors">How It Works</button></li>
                <li><button onClick={() => scrollToSection('features')} className="hover:text-[hsl(340,65%,60%)] transition-colors">Features</button></li>
                <li><button onClick={() => scrollToSection('faq')} className="hover:text-[hsl(340,65%,60%)] transition-colors">FAQ</button></li>
              </ul>
            </div>
            
            {/* Company */}
            <div>
              <h4 className="font-medium text-[hsl(168,55%,30%)] mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="#" className="hover:text-[hsl(340,65%,60%)] transition-colors">About</a></li>
                <li><a href="#" className="hover:text-[hsl(340,65%,60%)] transition-colors">Contact</a></li>
                <li><a href="#" className="hover:text-[hsl(340,65%,60%)] transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-[hsl(340,65%,60%)] transition-colors">Terms of Service</a></li>
              </ul>
            </div>
            
            {/* Connect */}
            <div>
              <h4 className="font-medium text-[hsl(168,55%,30%)] mb-4">Connect</h4>
              <div className="flex gap-4 mb-4">
                <a href="#" className="w-10 h-10 rounded-full bg-gradient-to-br from-[hsl(340,65%,60%)] to-[hsl(340,70%,50%)] flex items-center justify-center hover:shadow-lg transition-all text-white">
                  <Instagram className="w-5 h-5" />
                </a>
                <a href="#" className="w-10 h-10 rounded-full bg-gradient-to-br from-[hsl(168,55%,45%)] to-[hsl(168,60%,35%)] flex items-center justify-center hover:shadow-lg transition-all text-white">
                  <Mail className="w-5 h-5" />
                </a>
              </div>
              <p className="text-xs text-muted-foreground">
                Get skincare tips & product alerts
              </p>
            </div>
          </div>
          
          <div className="border-t border-border mt-12 pt-8 text-center text-sm text-muted-foreground">
            © 2026 SkinIQ. Made with <Heart className="w-4 h-4 inline text-[hsl(340,65%,60%)]" /> for skincare lovers everywhere.
          </div>
        </div>
      </footer>

      {/* Auth Dialog */}
      <Dialog open={showAuth} onOpenChange={(open) => {
        setShowAuth(open);
        if (!open) {
          setRegistrationStep(1);
          setSelectedSkinType(null);
          setShowForgotPassword(false);
          setForgotSent(false);
          setForgotEmail("");
        }
      }}>
        <DialogContent className="sm:max-w-md glass border-white/40" data-testid="auth-dialog" aria-describedby="auth-description">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-center bg-gradient-to-r from-[hsl(168,55%,40%)] to-[hsl(340,65%,60%)] bg-clip-text text-transparent">
              {showForgotPassword ? "Reset Password" : "Welcome to SkinIQ!"}
            </DialogTitle>
            <p id="auth-description" className="sr-only">Authentication dialog for signing in or creating an account</p>
          </DialogHeader>

          {showForgotPassword ? (
            <div className="space-y-4">
              {!forgotSent ? (
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <p className="text-sm text-muted-foreground text-center">
                    Enter your email and we'll send you a reset link.
                  </p>
                  <div className="space-y-2">
                    <Label htmlFor="forgot-email">Email</Label>
                    <Input
                      id="forgot-email"
                      type="email"
                      placeholder="you@example.com"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      className="input-skiniq"
                      data-testid="forgot-email-input"
                    />
                  </div>
                  <Button type="submit" className="w-full btn-primary" disabled={isLoading} data-testid="forgot-submit-btn">
                    {isLoading ? "Sending..." : "Send Reset Link"}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full"
                    onClick={() => setShowForgotPassword(false)}
                  >
                    Back to Sign In
                  </Button>
                </form>
              ) : (
                <div className="text-center space-y-4">
                  <CheckCircle className="w-12 h-12 text-[hsl(168,55%,45%)] mx-auto" />
                  <p className="text-sm text-muted-foreground">
                    Check your email for a password reset link!
                  </p>
                  <Button
                    variant="ghost"
                    className="w-full"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setForgotSent(false);
                      setForgotEmail("");
                    }}
                  >
                    Back to Sign In
                  </Button>
                </div>
              )}
            </div>
          ) : registrationStep === 1 ? (
            <Tabs defaultValue="signin" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="signin" data-testid="signin-tab">Sign In</TabsTrigger>
                <TabsTrigger value="signup" data-testid="signup-tab">Sign Up</TabsTrigger>
              </TabsList>

              <TabsContent value="signin">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="input-skiniq"
                      data-testid="login-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="input-skiniq"
                      data-testid="login-password-input"
                    />
                  </div>
                  <Button type="submit" className="w-full btn-primary" disabled={isLoading} data-testid="login-submit-btn">
                    {isLoading ? "Signing in..." : "Sign In"}
                  </Button>
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="w-full text-sm text-[hsl(340,65%,60%)] hover:underline"
                  >
                    Forgot your password?
                  </button>
                </form>
              </TabsContent>

              <TabsContent value="signup">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-email">Email</Label>
                    <Input
                      id="register-email"
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="input-skiniq"
                      data-testid="register-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-password">Password</Label>
                    <Input
                      id="register-password"
                      type="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="input-skiniq"
                      data-testid="register-password-input"
                    />
                  </div>
                  <Button type="submit" className="w-full btn-primary" disabled={isLoading} data-testid="register-submit-btn">
                    {isLoading ? "Creating account..." : "Create Account"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          ) : (
            <div className="space-y-6">
              <p className="text-center text-muted-foreground">
                What's your skin type? This helps us personalize your experience!
              </p>
              <div className="grid gap-3">
                {skinTypes.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => handleSkinTypeSelect(type.id)}
                    disabled={isLoading}
                    className={`p-4 rounded-2xl border-2 text-left transition-all ${
                      selectedSkinType === type.id
                        ? "border-[hsl(168,55%,45%)] bg-[hsl(168,50%,97%)]"
                        : "border-border hover:border-[hsl(340,65%,70%)]"
                    }`}
                    data-testid={`skin-type-${type.id}`}
                  >
                    <div className="font-medium text-[hsl(168,55%,30%)]">{type.name}</div>
                    <div className="text-sm text-muted-foreground">{type.description}</div>
                  </button>
                ))}
              </div>
              {isLoading && (
                <p className="text-center text-sm text-muted-foreground">Setting up your profile...</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
